'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaTag, FaMoneyBill, FaChartLine, FaUsers, FaPercentage, FaQuestionCircle, FaInfoCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';
import { toast } from 'react-hot-toast';

const DiscountCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [finalPrice, setFinalPrice] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { theme } = useTheme();

  const calculateDiscount = () => {
    setError('');
    setLoading(true);

    const op = parseFloat(originalPrice);
    const dp = parseFloat(discountPercentage);
    const fp = parseFloat(finalPrice);

    if (!op || (dp && !fp)) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (isNaN(op) || (dp && isNaN(dp)) || (fp && isNaN(fp))) {
      setError('Please enter valid numbers');
      setLoading(false);
      return;
    }

    if (op <= 0) {
      setError('Original price must be greater than 0');
      setLoading(false);
      return;
    }

    if (dp && (dp < 0 || dp > 100)) {
      setError('Discount percentage must be between 0 and 100%');
      setLoading(false);
      return;
    }

    if (fp && fp > op) {
      setError('Final price cannot be greater than original price');
      setLoading(false);
      return;
    }

    // Calculate based on available inputs
    let discountAmount, discountPercent;
    if (dp) {
      discountAmount = (op * dp) / 100;
      discountPercent = dp;
    } else {
      discountAmount = op - fp;
      discountPercent = ((op - fp) / op) * 100;
    }

    const finalPriceCalc = op - discountAmount;
    const savings = discountAmount;
    
    // Calculate additional metrics
    const metrics = {
      discountPerItem: discountAmount,
      savingsPerItem: savings,
      finalPricePerItem: finalPriceCalc,
      originalPricePerItem: op
    };

    // Format results
    const formattedResults = {
      discountAmount: formatCurrency(discountAmount),
      discountPercent: discountPercent.toFixed(2) + '%',
      finalPrice: formatCurrency(finalPriceCalc),
      savings: formatCurrency(savings),
      originalPrice: formatCurrency(op),
      ...metrics
    };

    // Set results
    setResult(formattedResults);

    // Update chart data
    const chartValues = {
      'Original Price': op,
      'Discount Amount': discountAmount,
      'Final Price': finalPriceCalc
    };

    setChartData({
      options: {
        chart: {
          type: 'donut',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        theme: {
          mode: theme === 'dark' ? 'dark' : 'light'
        },
        labels: Object.keys(chartValues),
        colors: ['#2196F3', '#FFC107', '#4CAF50'],
        legend: {
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      series: Object.values(chartValues)
    });

    setLoading(false);
    toast.success('Calculation complete!');
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Discount Calculator - Calculate Your Savings</title>
        <meta name="description" content="Calculate discounts, savings, and final prices with our advanced discount calculator. Includes visual charts and detailed analysis." />
        <meta property="og:title" content="Discount Calculator" />
        <meta property="og:description" content="Calculate discounts, savings, and final prices with our advanced discount calculator. Includes visual charts and detailed analysis." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Discount Calculator" />
        <meta name="twitter:description" content="Calculate discounts, savings, and final prices with our advanced discount calculator. Includes visual charts and detailed analysis." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-purple-500 dark:text-purple-400" />
              <h2 className="text-2xl font-bold ml-3">Discount Calculator</h2>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg mb-4">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); calculateDiscount(); }} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaTag className="text-purple-500 dark:text-purple-400" />
                  <input
                    type="number"
                    placeholder="Original Price (₹)"
                    value={originalPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setOriginalPrice(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaPercentage className="text-yellow-500 dark:text-yellow-400" />
                  <input
                    type="number"
                    placeholder="Discount Percentage (%)"
                    value={discountPercentage}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setDiscountPercentage(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaMoneyBill className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Final Price (₹)"
                    value={finalPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFinalPrice(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-yellow-500 py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black mr-2"></div>
                    Calculating...
                  </div>
                ) : (
                  'Calculate Discount'
                )}
              </button>

              {result && (
                <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Your Discount Details:</h3>
                  <div className="space-y-2">
                    <p className="text-purple-800 dark:text-purple-400 text-lg">Discount Amount: {result.discountAmount}</p>
                    <p className="text-yellow-800 dark:text-yellow-400">Discount Percentage: {result.discountPercent}</p>
                    <p className="text-green-800 dark:text-green-400">Final Price: {result.finalPrice}</p>
                    <p className="text-blue-800 dark:text-blue-400">Savings: {result.savings}</p>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Price Breakdown</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Chart
                    options={chartData.options}
                    series={chartData.series}
                    type="donut"
                    width={600}
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Original Price:</strong> {result.originalPrice}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Discount Amount:</strong> {result.discountAmount}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Final Price:</strong> {result.finalPrice}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaTag className="text-purple-500 dark:text-purple-400" />
              <h4 className="mt-3 font-semibold">Flexible Input</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Calculate by entering either discount percentage or final price</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaMoneyBill className="text-green-500 dark:text-green-400" />
              <h4 className="mt-3 font-semibold">Real-time Analysis</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Get instant calculations and visual representations</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaChartLine className="text-yellow-500 dark:text-yellow-400" />
              <h4 className="mt-3 font-semibold">Visual Charts</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">See price breakdown in interactive charts</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaInfoCircle className="text-blue-500 dark:text-blue-400" />
              <h4 className="mt-3 font-semibold">Detailed Breakdown</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Get comprehensive analysis of your discount</p>
            </motion.div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Smart Shopping</h4>
              <p className="text-gray-600 dark:text-gray-400">Make informed decisions about your purchases</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Cost Optimization</h4>
              <p className="text-gray-600 dark:text-gray-400">Identify the best deals and maximize savings</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Budget Planning</h4>
              <p className="text-gray-600 dark:text-gray-400">Plan your shopping budget effectively</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Comparison Tool</h4>
              <p className="text-gray-600 dark:text-gray-400">Compare different purchase scenarios</p>
            </motion.div>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <ol className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="border-l-4 border-purple-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-purple-500 mr-3">1</span>
                  <h4 className="font-semibold">Enter Original Price</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Input the original price of the item before any discounts.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If the item costs ₹5,000, enter 5000</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="border-l-4 border-purple-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-purple-500 mr-3">2</span>
                  <h4 className="font-semibold">Choose Calculation Method</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Enter either the discount percentage or the final price.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: Enter 30% discount or ₹3,500 final price</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="border-l-4 border-purple-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-purple-500 mr-3">3</span>
                  <h4 className="font-semibold">Calculate Discount</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Click the "Calculate Discount" button to see your results.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: For OP = ₹5000, DP = 30%, your discount would be ₹1,500</p>
              </motion.div>
            </ol>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">What is a discount percentage?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">A discount percentage represents the amount of reduction from the original price, expressed as a percentage.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">How does this calculator work?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">The calculator helps you calculate discounts by considering the original price and either the discount percentage or final price.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">Can I use this for international prices?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Yes, you can use this calculator for any currency. The results will be displayed in the currency you entered.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">How accurate are the calculations?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">The calculator provides exact calculations based on the inputs you provide.</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountCalculator;
